import { ReportData, SalesSource } from '../types/reportTypes';
import { combineReports } from '../utils/reportAggregationUtils';
import { julyData } from './julyData';
import { augustData } from './augustData';
import { septemberData } from './septemberData';

// All monthly reports to aggregate for Q3 2025
const allMonthlyReports = [
  julyData,
  augustData,
  septemberData
];

// Combine all three months into Q3 data
const combinedData = combineReports(allMonthlyReports, 'Q3 2025 Standard Report');

// Export the Q3 data with proper period information
export const q3Data: ReportData = {
  ...combinedData,
  period: {
    month: 7, // First month of Q3 (July)
    year: 2025,
    quarter: 3,
    startMonth: 7,
    endMonth: 9
  }
};

// Additional data for trend analysis in the quarterly report
export interface Q3TrendData {
  monthlyTrends: {
    month: string;
    totalSales: number;
    grossProfit: number;
    netProfit: number;
    adSpend: number;
    roi: number;
    grossMarginPct: number;
    netMarginPct: number;
    salesCount: number;
    avgSaleValue: number;
  }[];
  sourcePerformance: SourcePerformance[];
  leadSourceTrends: {
    source: string;
    july: { sales: number; count: number; roi: number };
    august: { sales: number; count: number; roi: number };
    september: { sales: number; count: number; roi: number };
    q3Total: { sales: number; count: number; roi: number };
  }[];
}

// Extended interface for source performance
interface SourcePerformance extends SalesSource {
  adSpend: number;
  roi: number;
  costPerSale: number;
}

// Generate trend data for Q3 analysis
export const q3TrendData: Q3TrendData = {
  monthlyTrends: [
    {
      month: "July",
      totalSales: julyData.kpis.totalSales,
      grossProfit: julyData.kpis.grossProfit,
      netProfit: julyData.kpis.netProfit,
      adSpend: julyData.kpis.adSpend,
      roi: julyData.kpis.adSpend > 0 ? julyData.kpis.grossProfit / julyData.kpis.adSpend : 0,
      grossMarginPct: julyData.kpis.grossProfitPercent,
      netMarginPct: julyData.kpis.netProfitPercent,
      salesCount: julyData.jobData.length,
      avgSaleValue: julyData.kpis.totalSales / julyData.jobData.length
    },
    {
      month: "August",
      totalSales: augustData.kpis.totalSales,
      grossProfit: augustData.kpis.grossProfit,
      netProfit: augustData.kpis.netProfit,
      adSpend: augustData.kpis.adSpend,
      roi: augustData.kpis.adSpend > 0 ? augustData.kpis.grossProfit / augustData.kpis.adSpend : 0,
      grossMarginPct: augustData.kpis.grossProfitPercent,
      netMarginPct: augustData.kpis.netProfitPercent,
      salesCount: augustData.jobData.length,
      avgSaleValue: augustData.kpis.totalSales / augustData.jobData.length
    },
    {
      month: "September",
      totalSales: septemberData.kpis.totalSales,
      grossProfit: septemberData.kpis.grossProfit,
      netProfit: septemberData.kpis.netProfit,
      adSpend: septemberData.kpis.adSpend,
      roi: septemberData.kpis.adSpend > 0 ? septemberData.kpis.grossProfit / septemberData.kpis.adSpend : 0,
      grossMarginPct: septemberData.kpis.grossProfitPercent,
      netMarginPct: septemberData.kpis.netProfitPercent,
      salesCount: septemberData.jobData.length,
      avgSaleValue: septemberData.kpis.totalSales / septemberData.jobData.length
    }
  ],

  // Calculate source performance with ROI
  sourcePerformance: (() => {
    const salesBySource = combinedData.salesBySource;
    const adSpendBySource = combinedData.adSpendBySource;

    return salesBySource.map(source => {
      const adSpendItem = adSpendBySource.find(ad =>
        ad.source.toLowerCase().includes(source.source.toLowerCase()) ||
        source.source.toLowerCase().includes(ad.source.toLowerCase())
      );

      const adSpend = adSpendItem?.spend || 0;
      const roi = adSpend > 0 ? source.grossProfit / adSpend : 0;
      const costPerSale = source.count > 0 ? adSpend / source.count : 0;

      return {
        ...source,
        adSpend,
        roi,
        costPerSale
      };
    }).sort((a, b) => b.roi - a.roi);
  })(),

  // Generate lead source trends
  leadSourceTrends: (() => {
    // Get unique sources across all months
    const allSources = new Set<string>();
    [julyData.salesBySource, augustData.salesBySource, septemberData.salesBySource].forEach(monthSources => {
      monthSources.forEach(source => allSources.add(source.source));
    });

    return Array.from(allSources).map(sourceName => {
      // Find data for each month
      const julySource = julyData.salesBySource.find(s => s.source === sourceName) ||
                         { totalSales: 0, count: 0, grossProfit: 0 };
      const augustSource = augustData.salesBySource.find(s => s.source === sourceName) ||
                           { totalSales: 0, count: 0, grossProfit: 0 };
      const septemberSource = septemberData.salesBySource.find(s => s.source === sourceName) ||
                              { totalSales: 0, count: 0, grossProfit: 0 };

      // Find ad spend for ROI calculation
      const julyAdSpend = julyData.adSpendBySource.find(ad =>
        ad.source.toLowerCase().includes(sourceName.toLowerCase()) ||
        sourceName.toLowerCase().includes(ad.source.toLowerCase())
      )?.spend || 0;

      const augustAdSpend = augustData.adSpendBySource.find(ad =>
        ad.source.toLowerCase().includes(sourceName.toLowerCase()) ||
        sourceName.toLowerCase().includes(ad.source.toLowerCase())
      )?.spend || 0;

      const septemberAdSpend = septemberData.adSpendBySource.find(ad =>
        ad.source.toLowerCase().includes(sourceName.toLowerCase()) ||
        sourceName.toLowerCase().includes(ad.source.toLowerCase())
      )?.spend || 0;

      const totalAdSpend = julyAdSpend + augustAdSpend + septemberAdSpend;
      const totalGrossProfit = julySource.grossProfit + augustSource.grossProfit + septemberSource.grossProfit;

      return {
        source: sourceName,
        july: {
          sales: julySource.totalSales,
          count: julySource.count,
          roi: julyAdSpend > 0 ? julySource.grossProfit / julyAdSpend : 0
        },
        august: {
          sales: augustSource.totalSales,
          count: augustSource.count,
          roi: augustAdSpend > 0 ? augustSource.grossProfit / augustAdSpend : 0
        },
        september: {
          sales: septemberSource.totalSales,
          count: septemberSource.count,
          roi: septemberAdSpend > 0 ? septemberSource.grossProfit / septemberAdSpend : 0
        },
        q3Total: {
          sales: julySource.totalSales + augustSource.totalSales + septemberSource.totalSales,
          count: julySource.count + augustSource.count + septemberSource.count,
          roi: totalAdSpend > 0 ? totalGrossProfit / totalAdSpend : 0
        }
      };
    }).sort((a, b) => b.q3Total.sales - a.q3Total.sales);
  })()
};
